// src/components/MarketInfoDialog.jsx
//
// The info button beside a market panel's publication note, and the dialog it
// opens: for every product in the selected market, when the auction closes,
// when WE publish, how much margin that leaves, and which delivery periods the
// auction actually clears.
//
// WHY IT SHOWS TWO TIMEZONES. The coupled auctions are defined in CET by
// SDAC/SIDC; the balancing gate is defined in EET by ADMIE. Forcing either one
// into the other's zone presents a converted number as if the source had
// written it. So both are shown, the native one is marked "as published", and
// the toggle only changes which is in the emphasised column.
//
// EVERY TIME HERE IS COMPUTED FOR THE SELECTED DELIVERY DAY, not formatted from
// a stored string, so the margins stay right across a DST transition and the
// panel cannot drift away from the schedule it documents.
import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import {
  CET_TZ,
  MARKET_TZ,
  formatGap,
  formatInZone,
  getMarketProducts,
  resolveEvent,
  zoneAbbrev,
} from "../constants/marketSchedule";

const PRINT_STYLE_ID = "market-info-print-style";

// Printing is how this becomes a PDF: the browser's own "Save as PDF" needs no
// dependency, no server round-trip and no bitmap. Everything outside the dialog
// is hidden for the duration.
const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .market-info-print, .market-info-print * { visibility: visible !important; }
  .market-info-print {
    position: absolute !important;
    left: 0; top: 0; width: 100%;
    box-shadow: none !important;
    color: #000 !important;
    background: #fff !important;
  }
  .market-info-print .market-info-noprint { display: none !important; }
  .market-info-print * { color: #000 !important; background: transparent !important; }
}
`;

function ensurePrintStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = PRINT_STYLE_ID;
  el.appendChild(document.createTextNode(PRINT_CSS));
  document.head.appendChild(el);
}

function TimePair({ instant, primaryTz, secondaryTz, nativeTz }) {
  if (!instant) return <span>—</span>;
  const rows = [primaryTz, secondaryTz].map((tz) => ({
    tz,
    time: formatInZone(instant, tz),
    abbrev: zoneAbbrev(instant, tz),
    isNative: nativeTz === tz,
  }));
  return (
    <Stack spacing={0.25}>
      {rows.map((r, i) => (
        <Stack key={r.tz} direction="row" spacing={0.75} alignItems="baseline">
          <Typography
            component="span"
            sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontVariantNumeric: "tabular-nums",
              fontWeight: i === 0 ? 700 : 500,
              fontSize: i === 0 ? "0.98rem" : "0.82rem",
              color: i === 0 ? "text.primary" : "text.secondary",
            }}
          >
            {r.time}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: "0.72rem", color: "text.secondary", letterSpacing: "0.04em" }}
          >
            {r.abbrev}
          </Typography>
          {r.isNative && (
            <Typography
              component="span"
              sx={{ fontSize: "0.66rem", color: "text.secondary", opacity: 0.85, fontStyle: "italic" }}
            >
              as published
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function ProductBlock({ product, deliveryDateKey, primaryTz, secondaryTz }) {
  const gateAt = resolveEvent(product.gate, deliveryDateKey);
  const runAt = resolveEvent(product.forecast, deliveryDateKey);
  const margin = formatGap(runAt, gateAt);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25, flexWrap: "wrap", rowGap: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1.02rem" }}>{product.title}</Typography>
        {margin && (
          <Chip
            size="small"
            label={`${margin} of margin`}
            sx={{ height: 21, fontWeight: 700, fontSize: "0.72rem" }}
            color="success"
            variant="outlined"
          />
        )}
        {product.gateKind === "internal" && (
          <Chip
            size="small"
            label="our own cut-off"
            variant="outlined"
            sx={{ height: 21, fontSize: "0.7rem" }}
          />
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 190px) 1fr" },
          columnGap: 2,
          rowGap: 1.25,
          alignItems: "start",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          We publish
        </Typography>
        <Box>
          <TimePair instant={runAt} primaryTz={primaryTz} secondaryTz={secondaryTz} nativeTz={null} />
          {product.forecastNote && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {product.forecastNote}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          {product.gateKind === "internal" ? "Delivery begins" : "Gate closes"}
        </Typography>
        <Box>
          <TimePair
            instant={gateAt}
            primaryTz={primaryTz}
            secondaryTz={secondaryTz}
            nativeTz={product.nativeTz}
          />
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {product.authority}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Periods traded
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: product.periodsEmphasis ? "warning.main" : "text.primary",
            fontWeight: product.periodsEmphasis ? 600 : 400,
          }}
        >
          {product.periods}
        </Typography>

        {product.results && (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Results
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {product.results}
            </Typography>
          </>
        )}

        {product.caveats && product.caveats.length > 0 && (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Worth knowing
            </Typography>
            <Stack spacing={0.75}>
              {product.caveats.map((c) => (
                <Typography key={c} variant="body2" sx={{ color: "text.secondary" }}>
                  {c}
                </Typography>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function MarketInfoDialog({ market, deliveryDate, size = "small" }) {
  const [open, setOpen] = useState(false);
  const [zoneMode, setZoneMode] = useState("market");

  const products = useMemo(() => getMarketProducts(market), [market]);
  const marketTz = MARKET_TZ[market] || "Europe/Athens";
  const primaryTz = zoneMode === "cet" ? CET_TZ : marketTz;
  const secondaryTz = zoneMode === "cet" ? marketTz : CET_TZ;

  // A concrete day so every time below is a real instant. Falls back to today
  // when the caller has no date selected.
  const deliveryDateKey = useMemo(() => {
    if (deliveryDate) return deliveryDate;
    const now = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  }, [deliveryDate]);

  if (!products.length) return null;

  const openDialog = () => {
    ensurePrintStyle();
    setOpen(true);
  };

  return (
    <>
      <Tooltip title="Market timings and what we publish" arrow>
        <IconButton
          size={size}
          onClick={openDialog}
          aria-label="Market timings and what we publish"
          sx={{ p: 0.35, color: "text.secondary" }}
        >
          <InfoOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: "market-info-print", sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.18rem", lineHeight: 1.25 }}>
                {market} market timings
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                Delivery day {deliveryDateKey}. Every time below is computed for that day, so DST is
                already in it.
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" className="market-info-noprint">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={zoneMode}
                onChange={(_e, v) => v && setZoneMode(v)}
              >
                <ToggleButton value="market" sx={{ textTransform: "none", px: 1.25 }}>
                  Market time
                </ToggleButton>
                <ToggleButton value="cet" sx={{ textTransform: "none", px: 1.25 }}>
                  CET
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Print or save as PDF" arrow>
                <IconButton size="small" onClick={() => window.print()} aria-label="Print or save as PDF">
                  <PrintOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            The day-ahead and intraday gates are written in CET by SDAC and SIDC; the balancing gate
            is written in EET by ADMIE. Both are shown, and the one the source itself publishes is
            marked.
          </Typography>
          <Divider />
          {products.map((p, i) => (
            <React.Fragment key={p.key}>
              {i > 0 && <Divider />}
              <ProductBlock
                product={p}
                deliveryDateKey={deliveryDateKey}
                primaryTz={primaryTz}
                secondaryTz={secondaryTz}
              />
            </React.Fragment>
          ))}
          <Divider />
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", pt: 1.5, lineHeight: 1.7 }}>
            Gate times as published by SDAC and SIDC (EPEX SPOT trading products), HEnEx Technical
            Decision 10, and ADMIE's Integrated Scheduling Process. Publication times are this
            platform's own scheduled runs; each has a retry chain that stops before its gate rather
            than crossing it.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
